import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const VAR_MAP = {
  primary: '--primary', on_primary: '--on-primary', accent: '--accent',
  bg: '--bg', fg: '--fg', card: '--card', card_fg: '--card-fg',
  muted: '--muted', muted_fg: '--muted-fg', border: '--border',
};
const MODE_KEY = 'site-theme-mode'; // light | dark | auto

const isLightBg = (hex) => {
  const m = /^#([0-9a-fA-F]{6})$/.exec((hex || '').trim());
  if (!m) return true;
  const n = parseInt(m[1], 16);
  return (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) > 140;
};

const hexToRgba = (hex, alpha) => {
  const m = /^#([0-9a-fA-F]{6})$/.exec((hex || '').trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/** توکن‌های شکل (فرم دکمه/فیلد/کارت/کادر) از سیستم طراحی فعال. */
const shapeDefaults = { btn: 12, input: 12, card: 16, line: 1, lineInput: 1, btnH: 40, inputH: 40 };
export function shapeVars(shape) {
  const s = { ...shapeDefaults, ...(shape || {}) };
  return {
    '--shape-btn': `${s.btn}px`,
    '--shape-input': `${s.input}px`,
    '--shape-card': `${s.card}px`,
    '--shape-line': `${s.line}px`,
    '--shape-line-input': `${s.lineInput ?? s.line}px`,
    '--shape-btn-h': `${s.btnH}px`,
    '--shape-input-h': `${s.inputH}px`,
  };
}

/**
 * خروجی توکن‌های تم به‌صورت شیء style — برای اعمال محدوده‌ای (مثلاً بوم استودیو)
 * بدون تغییر ظاهر خود پنل ادمین.
 */
export function themeVarsStyle(theme, mode) {
  const eff = mode === 'dark' ? { ...theme?.light, ...theme?.dark } : (theme?.light || {});
  const glassy = theme?.style === 'glass' || theme?.style === 'liquid';
  const alpha = theme?.style === 'liquid' ? 0.5 : 0.62;
  const out = {};
  Object.entries(VAR_MAP).forEach(([k, v]) => {
    if (!eff[k]) return;
    let val = eff[k];
    if (glassy && (k === 'card' || k === 'muted')) val = hexToRgba(val, k === 'muted' ? Math.max(0.35, alpha - 0.12) : alpha);
    if (glassy && k === 'border') val = hexToRgba(val, 0.28);
    out[v] = val;
  });
  if (eff.radius !== undefined && eff.radius !== null && eff.radius !== '') out['--radius'] = `${eff.radius}px`;
  if (eff.font) out['--font-sans'] = `'${eff.font}', sans-serif`;
  const light = mode !== 'dark';
  out['--ok'] = light ? '#15803D' : '#4ADE80';
  out['--ok-bg'] = light ? 'rgba(21,128,61,0.12)' : 'rgba(74,222,128,0.14)';
  Object.assign(out, shapeVars(theme?.shape));
  return out;
}

export function applyThemeVars(set = {}, style = '', shape = null) {
  const root = document.documentElement;
  const glassy = style === 'glass' || style === 'liquid';
  const alpha = style === 'liquid' ? 0.5 : 0.62;
  Object.entries(VAR_MAP).forEach(([k, v]) => {
    if (!set[k]) return;
    let val = set[k];
    // سطوح شیشه‌ای: کارت/ملایم نیمه‌شفاف
    if (glassy && (k === 'card' || k === 'muted')) val = hexToRgba(val, k === 'muted' ? Math.max(0.35, alpha - 0.12) : alpha);
    if (glassy && k === 'border') val = hexToRgba(val, 0.28);
    root.style.setProperty(v, val);
  });
  if (set.radius !== undefined && set.radius !== null && set.radius !== '') {
    root.style.setProperty('--radius', `${set.radius}px`);
  }
  if (set.font) {
    root.style.setProperty('--font-sans', `'${set.font}', sans-serif`);
  }
  // زبان فرم: شکل دکمه/فیلد/کارت/کادر از سیستم طراحی فعال
  Object.entries(shapeVars(shape)).forEach(([k, v]) => root.style.setProperty(k, v));
  // رنگ وضعیت موفقیت — با کنتراست کنترل‌شده نسبت به روشنایی پس‌زمینه
  const lightBg = isLightBg(set.bg);
  root.style.setProperty('--ok', lightBg ? '#15803D' : '#4ADE80');
  root.style.setProperty('--ok-bg', lightBg ? 'rgba(21,128,61,0.12)' : 'rgba(74,222,128,0.14)');
  // توکن‌های معنایی (کلاس‌های Tailwind مثل bg-card/text-foreground) از تم سایت
  const onAccent = isLightBg(set.accent) ? '#1B0505' : '#FFF6E8';
  const semantic = {
    '--background': set.bg, '--foreground': set.fg,
    '--card': set.card, '--card-foreground': set.card_fg,
    '--popover': set.card, '--popover-foreground': set.card_fg,
    '--primary-foreground': set.on_primary,
    '--secondary': set.muted, '--secondary-foreground': set.muted_fg,
    '--muted-foreground': set.muted_fg, '--accent-foreground': onAccent,
    '--border': set.border, '--input': set.border, '--ring': set.primary,
  };
  Object.entries(semantic).forEach(([k, v]) => { if (v) root.style.setProperty(k, v); });
  root.style.colorScheme = lightBg ? 'light' : 'dark';
  root.classList.toggle('light', lightBg);
  root.classList.toggle('dark', !lightBg);
  // سبک ظاهری: مینیمال / شیشه / لیکویید گلس
  root.classList.remove('style-minimal', 'style-glass', 'style-liquid', 'style-neomorph', 'style-flat', 'style-gradient', 'style-glow', 'style-soft', 'style-luxury');
  const styleMap = { glass: 'style-glass', liquid: 'style-liquid', neomorph: 'style-neomorph', flat: 'style-flat', gradient: 'style-gradient', glow: 'style-glow', soft: 'style-soft', luxury: 'style-luxury' };
  root.classList.add(styleMap[style] || 'style-minimal');
  // ریزه‌کاری‌های داخلی ویجت‌ها (دکمه/بج/آیکون/تیتر/کارت) با سبک ظاهری عوض می‌شوند
  const variant = style === 'glass' ? 'glass' : style === 'liquid' ? 'liquid' : 'minimal';
  const detail = {
    minimal: {
      '--chip-bg': 'transparent', '--chip-border': '1px solid var(--border)', '--chip-radius': '10px',
      '--icon-bg': 'color-mix(in srgb, var(--primary) 10%, transparent)', '--icon-radius': '12px',
      '--btn-radius-i': 'var(--shape-btn)', '--btn-weight': '600', '--title-weight': '700',
      '--card-line': '1px solid var(--border)', '--card-shadow-i': 'none', '--surface-blur-i': 'none', '--chip-fg': 'var(--fg)',
    },
    glass: {
      '--chip-bg': 'color-mix(in srgb, var(--card) 55%, transparent)', '--chip-border': '1px solid color-mix(in srgb, var(--border) 60%, transparent)', '--chip-radius': '14px',
      '--icon-bg': 'color-mix(in srgb, var(--primary) 18%, transparent)', '--icon-radius': '16px',
      '--btn-radius-i': '999px', '--btn-weight': '700', '--title-weight': '800',
      '--card-line': '1px solid color-mix(in srgb, var(--border) 55%, transparent)', '--card-shadow-i': '0 10px 34px rgba(20,16,10,0.14)', '--surface-blur-i': 'blur(16px) saturate(150%)', '--chip-fg': 'var(--fg)',
    },
    liquid: {
      '--chip-bg': 'color-mix(in srgb, var(--card) 72%, transparent)', '--chip-border': '1px solid color-mix(in srgb, var(--primary) 22%, transparent)', '--chip-radius': '18px',
      '--icon-bg': 'color-mix(in srgb, var(--primary) 22%, transparent)', '--icon-radius': '20px',
      '--btn-radius-i': '999px', '--btn-weight': '800', '--title-weight': '900',
      '--card-line': '1px solid color-mix(in srgb, var(--primary) 16%, var(--border))', '--card-shadow-i': '0 16px 44px rgba(20,16,10,0.18)', '--surface-blur-i': 'blur(22px) saturate(175%)', '--chip-fg': 'var(--fg)',
    },
  }[variant];
  Object.entries(detail).forEach(([k, v]) => root.style.setProperty(k, v));
  // glassmorphism tokens (optional, from theme defaults): blur floating cards/nav
  const gb = Number(set.glassBlur) || 0;
  if (set.glass && gb > 0) {
    const sat = Number(set.glassSaturate) || 150;
    root.style.setProperty('--glass-blur', `blur(${gb}px) saturate(${sat}%)`);
    root.classList.add('theme-glass');
  } else {
    root.classList.remove('theme-glass');
  }
}

export function currentModeOverride() {
  try { return localStorage.getItem(MODE_KEY) || 'auto'; } catch { return 'auto'; }
}
export function setModeOverride(mode) {
  try { localStorage.setItem(MODE_KEY, mode); } catch {}
  window.dispatchEvent(new Event('site-theme-changed'));
}

export function useSiteTheme() {
  const [theme, setTheme] = useState({ mode: 'light', allow_toggle: false, light: {}, dark: {}, shape: null });
  const [mode, setMode] = useState('light');

  const apply = useCallback((t, override) => {
    const eff = override !== 'auto' ? override : (t.mode || 'light');
    setMode(eff === 'dark' ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', eff === 'dark' ? 'dark' : 'light');
    applyThemeVars(eff === 'dark' ? { ...t.light, ...t.dark } : t.light, t.style || '', t.shape || null);
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () => {
      axios.get('/site-settings').then(r => {
        if (!alive) return;
        const t = r.data?.theme || { mode: 'light', allow_toggle: false, light: {}, dark: {}, shape: null };
        setTheme(t);
        apply(t, currentModeOverride());
      }).catch(() => {});
    };
    load();
    window.addEventListener('site-theme-changed', load);
    // اعمال درجا: تغییر از تب دیگر (پنل ادمین) یا برگشت به تب سایت
    const onStorage = e => { if (!e.key || e.key === 'site-theme-ping' || e.key === 'site-theme-mode') load(); };
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', load);
    window.addEventListener('focus', load);
    return () => {
      alive = false;
      window.removeEventListener('site-theme-changed', load);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', load);
      window.removeEventListener('focus', load);
    };
  }, [apply]);

  return { theme, mode, setModeOverride, allowToggle: !!theme.allow_toggle };
}

export default function ThemeProvider() {
  useSiteTheme();
  return null;
}
